/**
 * Today's reads, each over a `@repo/client/data/*` `QueryDef` and T11's
 * hooks. Every card owns its read, so one failing never blanks another.
 * Offline, each renders from the persisted Query cache (plan D17); a card
 * with nothing cached renders nothing.
 *
 * - `usePending()` (from the shell's `badges.tsx`): `GET /me/pending`, the
 *   same polled entry the nav badges read.
 * - `useStats()`: `GET /me/stats` for the week header and the streak.
 * - `useWeekSessions()`: `GET /me/sessions?from&to` for the strip's week,
 *   Sunday to Saturday.
 * - `useInsight()`: `GET /me/insight` in the app locale.
 * - `useDraftDiff(enabled)`: `GET /me/plan/draft/diff`, only while
 *   `pending.draft` says one exists.
 * - `useMomentWeek(week)`: `GET /me/retrospective?week=` for the ready week.
 */
import { ApiError } from "@repo/client/api";
import { draftDiffOptions } from "@repo/client/data/plan";
import {
  insightOptions,
  retrospectiveOptions,
} from "@repo/client/data/progress";
import {
  sessionsOptions,
  trainingStatsOptions,
} from "@repo/client/data/workout";
import type { DraftDiffOutput } from "@repo/contract/training";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../data/api";
import { useDataQuery } from "../../data/query-client";
import { coveringSessions } from "../../data/sessions-placeholder";
import { appI18n } from "../auth/i18n";
import { catalogueLocaleOf } from "../plan/use-plan";
import { weekRangeOf } from "./model";

export function useStats() {
  const api = useApi();
  return useDataQuery(trainingStatsOptions(api));
}

export function useWeekSessions(now: Date) {
  const api = useApi();
  const client = useQueryClient();
  const range = weekRangeOf(now);
  // A new week is a new key; the hero's cached 42 days cover it (`D190`).
  return useDataQuery(sessionsOptions(api, range), {
    placeholderData: () => coveringSessions(client, range),
  });
}

export function useInsight() {
  const api = useApi();
  const language = appI18n.useLocale();
  const locale = catalogueLocaleOf(language);
  return useDataQuery(insightOptions(api, { language, locale }));
}

/** The diff of the waiting draft; `null` when there is none to show. */
export function useDraftDiff(enabled: boolean): DraftDiffOutput | null {
  const api = useApi();
  const locale = catalogueLocaleOf(appI18n.useLocale());
  const diff = useDataQuery(draftDiffOptions(api, { locale }), { enabled });
  if (!enabled || diff.data === undefined) {
    return null;
  }
  // Published or discarded between the two reads: the 404 is "no draft".
  if (diff.error instanceof ApiError && diff.error.status === 404) {
    return null;
  }
  return diff.data;
}

export function useMomentWeek(week: string | null) {
  const api = useApi();
  const locale = catalogueLocaleOf(appI18n.useLocale());
  return useDataQuery(
    retrospectiveOptions(api, { locale, week: week ?? undefined }),
    { enabled: week !== null }
  );
}
